import { BookmarkButton } from '@mdvip/shared/components/blocks/BookmarkButton';
import type { ContentCardMeta } from '@mdvip/shared/components/blocks/ContentCard';
import { Icon } from '@mdvip/shared/components/blocks/Icon';
import { cn } from '@mdvip/shared/lib/utils';
import * as React from 'react';
import { Link } from 'react-router-dom';

const FOCUS =
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background';

export interface FeaturedContentCardProps {
  title: string;
  description?: string;
  href: string;
  /** Names the destination ("Read the article"), not a generic "Learn more". */
  ctaLabel: string;
  /** Small-caps label above the title (e.g. the content's source or kind). */
  kicker?: string;
  meta?: ContentCardMeta[];
  imageSrc?: string | null;
  imagePlaceholder?: string;
  savable?: boolean;
  saved?: boolean;
  onSaveChange?: (next: boolean) => void;
  className?: string;
}

/**
 * FeaturedContentCard — the photograph-led card a surface leads with when it features one piece of
 * content; use `ContentCard` for a grid or list of many.
 *
 * Stacked below a 720px container: the photograph, then the copy panel inset from it and pulled up
 * over its lower edge. From 720px: the photograph on the left with the panel overlapping its right
 * edge. The whole surface is one link (an overlay from the call to action), and the save control
 * sits on the photograph above it, independently reachable.
 */
export function FeaturedContentCard({
  title,
  description,
  href,
  ctaLabel,
  kicker,
  meta = [],
  imageSrc,
  imagePlaceholder,
  savable = false,
  saved = false,
  onSaveChange,
  className,
}: FeaturedContentCardProps) {
  const titleId = React.useId();
  const ctaId = React.useId();
  const image = imageSrc || imagePlaceholder;

  return (
    <article
      data-slot="featured-content-card"
      aria-labelledby={titleId}
      className={cn('@container relative isolate w-full', className)}
    >
      <div className="flex flex-col px-1 pb-1 @[720px]:flex-row @[720px]:items-center">
        <div className="relative aspect-[4/3] w-full overflow-hidden rounded-xl bg-muted @[720px]:aspect-[16/10] @[720px]:w-[55%]">
          {image && <img src={image} alt="" className="size-full object-cover" />}
          {savable && (
            <div className="absolute top-3 right-3 z-30 @[720px]:right-auto @[720px]:left-3">
              <BookmarkButton
                variant="icon"
                saved={saved}
                onSaveChange={(next) => onSaveChange?.(next)}
                itemTitle={title}
              />
            </div>
          )}
        </div>
        {/* Deliberately not `relative`: z-10 on a flex item already lifts the panel over the
            photograph, and leaving it unpositioned lets the CTA's overlay span the whole card. */}
        <div className="z-10 mx-4 -mt-12 flex flex-1 flex-col gap-3 rounded-xl border bg-card p-4 shadow-md @[720px]:mx-0 @[720px]:mt-0 @[720px]:-ml-24 @[720px]:w-[44%] @[720px]:flex-none @[720px]:p-8">
          {kicker && (
            <span className="text-xs font-semibold tracking-[0.12em] text-muted-foreground uppercase">{kicker}</span>
          )}
          <h3
            id={titleId}
            className="text-lg leading-snug font-semibold text-foreground @[720px]:text-xl @[900px]:text-2xl"
          >
            {title}
          </h3>
          {description && (
            <p className="text-sm leading-relaxed text-muted-foreground @[720px]:text-base">{description}</p>
          )}
          {meta.length > 0 && (
            <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-muted-foreground @[720px]:text-sm">
              {meta.map((m) => (
                <span key={m.label} className="flex items-center gap-2">
                  <Icon name={m.icon} size="xs" />
                  {m.label}
                </span>
              ))}
            </div>
          )}
          <div>
            <Link
              to={href}
              aria-labelledby={`${ctaId} ${titleId}`}
              className={cn(
                "inline-flex min-h-6 items-center gap-2 text-sm font-semibold text-foreground after:absolute after:inset-0 after:content-['']",
                FOCUS,
              )}
            >
              <span id={ctaId} className="underline decoration-from-font underline-offset-2">
                {ctaLabel}
              </span>
              <Icon name="chevronRight" size="xs" />
            </Link>
          </div>
        </div>
      </div>
    </article>
  );
}
